import { Select, Skeleton } from "antd";
import { offerApi } from "api/TenantSlug/Offer/api.ts";
import { useGetOfferLinksQuery } from "api/TenantSlug/OfferLink/hooks.ts";
import { useOfferUserList } from "components/Forms/shared/useOfferUserList";
import { FormCol } from "components/UI/Form/FormCol.tsx";
import { FormGrid } from "components/UI/Form/FormGrid.tsx";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider.tsx";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm.ts";
import {
    CustomDomainSelectSearchable,
    DEFAULT_DOMAIN_VALUE,
} from "components/UI/Selects/CustomDomainSelectSearchable/CustomDomainSelectSearchable.tsx";
import { useCustomDomainSelectOptions } from "components/UI/Selects/CustomDomainSelectSearchable/useCustomDomainSelectOptions.ts";
import { TagSelect } from "components/UI/TagSelect/TagSelect.tsx";
import { TextArea } from "components/UI/TextArea/TextArea.tsx";
import { useFetchAllPaginatedData } from "hooks/useFetchAllPaginatedData.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { usePermission } from "hooks/usePermissions.ts";
import { useUser } from "hooks/useUser.ts";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormItem } from "react-hook-form-antd";
import { PermissionEnum } from "types/enums.ts";
import { FormId } from "types/models.ts";

import { formSchema, OfferLinkFormData } from "./formSchema.ts";
import { buildOfferLinkUrl } from "./utils/buildOfferLinkUrl.ts";
import { filterOffersByAccess } from "./utils/filterOffersByAccess.ts";

import OfferLinkData = App.Data.OfferLinkData;
import OfferData = App.Data.OfferData;
import UserData = App.Data.UserData;

interface OfferLinkDetailFormContentProps {
    offerId?: string;
    showOfferSelect: boolean;
    showUserSelect: boolean;
    showDomainSelect: boolean;
    offers: OfferData[];
    users: UserData[];
    defaultUserId: string;
    isOffersLoading: boolean;
    onOfferChange?: (offerId: string) => void;
    onUrlChange?: (url: string) => void;
}

const OfferLinkDetailFormContent: FC<OfferLinkDetailFormContentProps> = ({
    offerId,
    showOfferSelect,
    showUserSelect,
    showDomainSelect,
    offers,
    users,
    defaultUserId,
    isOffersLoading,
    onOfferChange,
    onUrlChange,
}) => {
    const { t: tF } = usePaldockTranslation("forms.offer-link-detail");
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const { t: tTags } = usePaldockTranslation("general.tags");

    const generalFormUtils = useGeneralForm<OfferLinkFormData>();
    const { control, fieldNames, setValue, getValues } = generalFormUtils;

    const userUtils = useUser();

    // Helper to set URL and notify parent
    const setUrl = useCallback(
        (url: string) => {
            setValue("url", url);
            onUrlChange?.(url);
        },
        [setValue, onUrlChange]
    );

    const [selectedOfferId, setSelectedOfferId] = useState<string | undefined>(offerId);
    const [selectedUserId, setSelectedUserId] = useState<string | undefined>(defaultUserId);
    const [selectedDomainId, setSelectedDomainId] = useState<string>(DEFAULT_DOMAIN_VALUE);
    const [selectedTitleId, setSelectedTitleId] = useState<string | undefined>(undefined);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);

    const tagsOptions = [
        { label: tTags("affcid"), value: "affcid" },
        { label: tTags("affs1"), value: "affs1" },
        { label: tTags("affs2"), value: "affs2" },
        { label: tTags("affs3"), value: "affs3" },
        { label: tTags("affs4"), value: "affs4" },
        { label: tTags("affs5"), value: "affs5" },
        { label: tTags("affs6"), value: "affs6" },
        { label: tTags("affs7"), value: "affs7" },
        { label: tTags("affs8"), value: "affs8" },
        { label: tTags("affs9"), value: "affs9" },
        { label: tTags("affs10"), value: "affs10" },
        { label: tTags("destination"), value: "destination" },
    ];

    useEffect(() => {
        if (offerId) {
            setSelectedOfferId(offerId);
            setValue("offer", offerId);
        }
    }, [offerId, setValue]);

    useEffect(() => {
        if (showOfferSelect && !selectedOfferId && offers.length) {
            const first = offers[0];
            setSelectedOfferId(first.id);
            setValue("offer", first.id);
        }
    }, [showOfferSelect, offers, selectedOfferId, setValue]);

    const getOfferLinksQuery = useGetOfferLinksQuery(
        {
            filter: {
                filter: {
                    ...(selectedUserId && { owner_id: selectedUserId }),
                    ...(selectedOfferId && { offer_id: selectedOfferId }),
                },
                sort: "title",
            },
        },
        {
            enabled: !!selectedOfferId && !!selectedUserId,
        }
    );
    const offerLinks: OfferLinkData[] | undefined = getOfferLinksQuery.data?.offerLinks;

    // Hook for URL building logic
    const customDomainOptions = useCustomDomainSelectOptions();

    // Construct link with domain and title, preserving existing search params
    const constructLink = useCallback(
        (domain: string, titleId?: string) => {
            if (!offerLinks?.length) return "";

            const offerLink = offerLinks.find((l) => l.id === titleId) ?? offerLinks[0];
            if (!offerLink) return "";

            return buildOfferLinkUrl({
                currentUrl: getValues().url,
                customDomainOptions: customDomainOptions.options,
                defaultDomainValue: DEFAULT_DOMAIN_VALUE,
                domain,
                offerLinkSlug: offerLink.slug,
                tenantSlug: userUtils.getActiveTenant()?.slug_name,
                trackingUrl: import.meta.env.VITE_TRACKING_URL,
            });
        },
        [offerLinks, customDomainOptions.options, getValues, userUtils]
    );

    // Reset title when offer changes - use ref to track previous value
    // MUST be before the "set initial" effect so it runs first
    const prevOfferId = useRef(selectedOfferId);
    useEffect(() => {
        if (selectedOfferId && prevOfferId.current !== selectedOfferId) {
            setSelectedTitleId(undefined);
            setValue("title", undefined);
            // The URL itself is left as-is (not cleared) so the "set initial title and URL"
            // effect below can preserve its query params (tags/manual edits) via constructLink's
            // currentUrl once new offerLinks load for the new offer — same as domain/title/user
            // changes already do.
        }
        prevOfferId.current = selectedOfferId;
    }, [selectedOfferId, setValue]);

    // Set initial title and URL when offerLinks load
    useEffect(() => {
        if (!selectedDomainId) return;

        // Don't set anything while fetching new data (prevents using stale offerLinks)
        if (getOfferLinksQuery.isFetching) return;

        // If no offer links, clear URL and title
        if (!offerLinks?.length) {
            setUrl("");
            return;
        }

        const foundOfferLink = selectedTitleId ? offerLinks.find((l) => l.id === selectedTitleId) : undefined;
        const effectiveOfferLink = foundOfferLink ?? offerLinks[0];

        // Set/update title if not selected yet OR if selection no longer exists in offerLinks
        if ((!selectedTitleId || !foundOfferLink) && effectiveOfferLink) {
            setSelectedTitleId(effectiveOfferLink.id);
            setValue("title", effectiveOfferLink.id);
        }

        // Always set URL
        const link = constructLink(selectedDomainId, effectiveOfferLink?.id);
        setUrl(link);
    }, [offerLinks, selectedTitleId, selectedDomainId, constructLink, setValue, setUrl, getOfferLinksQuery.isFetching]);

    const isLoading =
        customDomainOptions.loading || getOfferLinksQuery.isLoading || getOfferLinksQuery.isFetching || isOffersLoading;

    const handleOfferChange = (val: string) => {
        setSelectedOfferId(val);
        onOfferChange?.(val);
        // Don't change user here - users prop hasn't updated yet
        // User will be updated by the effect below when users prop changes
    };

    // Update userId when users prop changes (after offer change)
    useEffect(() => {
        if (!users.length) return;

        // If current userId is not in new users list, reset to first user
        const currentUserInList = users.some((u) => u.id === selectedUserId);
        if (!currentUserInList) {
            const firstUser = users[0]?.id;
            if (firstUser) {
                setSelectedUserId(firstUser);
                setValue("user", firstUser);
                // Reset title like handleUserChange does — the URL itself is left as-is so the
                // "set initial title and URL" effect below can preserve its query params
                // (tags/manual edits) via constructLink's currentUrl once new offerLinks load,
                // same as it already does for domain/title changes.
                setSelectedTitleId(undefined);
                setValue("title", undefined);
            }
        }
    }, [users, selectedUserId, setValue]);

    const handleTitleChange = (val: string) => {
        setSelectedTitleId(val);
        const link = constructLink(selectedDomainId, val);
        setUrl(link);
    };

    const handleDomainChange = (val: string) => {
        setSelectedDomainId(val);
        const link = constructLink(val, selectedTitleId);
        setUrl(link);
    };

    const handleUserChange = (val: string) => {
        setSelectedUserId(val);
        setValue("user", val);
        // Reset title when user changes — the URL itself is left as-is so the "set initial title
        // and URL" effect below can preserve its query params (tags/manual edits) via
        // constructLink's currentUrl once new offerLinks load, same as domain/title changes do.
        setSelectedTitleId(undefined);
        setValue("title", undefined);
    };

    const changeTags = (tags: string[]) => {
        setSelectedTags(tags);

        const currentUrl = getValues().url;
        if (!currentUrl) return;

        let url: URL;
        try {
            url = new URL(currentUrl);
        } catch {
            // Invalid URL, cannot modify tags
            return;
        }

        const params = new URLSearchParams(url.search);

        // Remove params that are no longer in tags
        params.forEach((_, key) => {
            if (!tags.includes(key)) {
                params.delete(key);
            }
        });

        // Add new params for tags that don't exist
        tags.forEach((tag) => {
            if (!params.has(tag)) {
                params.append(tag, "REPLACE_ME");
            }
        });

        url.search = params.toString();
        setUrl(url.toString());
    };

    if (isLoading) return <FormSkeleton />;

    const hasOfferLinks = !!offerLinks?.length;
    const colsCount = 1 + (showOfferSelect ? 1 : 0) + (showUserSelect ? 1 : 0) + (showDomainSelect ? 1 : 0);
    const cols = Math.min(colsCount, 4) as 1 | 2 | 3 | 4;

    return (
        <FormCol>
            <FormGrid cols={cols}>
                {showOfferSelect && (
                    <FormItem control={control} name={fieldNames.offer?.name || "offer"} label={tFields("offer.label")}>
                        <Select
                            placeholder={tFields("offer.placeholder")}
                            options={offers.map((o) => ({ label: o.title ?? "no-title", value: o.id }))}
                            onChange={handleOfferChange}
                        />
                    </FormItem>
                )}
                <FormItem control={control} name={fieldNames.title?.name || "title"} label={tFields("title.label")}>
                    <Select
                        showSearch
                        value={selectedTitleId}
                        filterOption={(input, option) =>
                            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                        }
                        options={(offerLinks ?? []).map((l) => ({
                            label: l.title || "no-title",
                            value: l.id,
                        }))}
                        disabled={!hasOfferLinks}
                        placeholder={!hasOfferLinks ? tF("messages.no_active_links") : undefined}
                        onChange={handleTitleChange}
                    />
                </FormItem>
                {showDomainSelect && (
                    <FormItem
                        control={control}
                        name={fieldNames.domain?.name || "domain"}
                        label={tF("fields.domain.label")}
                    >
                        <CustomDomainSelectSearchable onChange={handleDomainChange} />
                    </FormItem>
                )}
                {showUserSelect && (
                    <FormItem control={control} name={fieldNames.user?.name || "user"} label={tFields("user.label")}>
                        <Select
                            showSearch
                            filterOption={(input, option) =>
                                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                            }
                            placeholder={tFields("user.placeholder")}
                            options={users.map((u) => ({
                                label: u.nickname ?? u.email ?? tFields("user.no_name"),
                                value: u.id,
                            }))}
                            onChange={handleUserChange}
                        />
                    </FormItem>
                )}
            </FormGrid>
            <FormGrid cols={1}>
                <FormItem control={control} name={fieldNames.url?.name || "url"} label={tF("fields.link.label")}>
                    <TextArea name="url" appearance="readonly" />
                </FormItem>
                <TagSelect options={tagsOptions} value={selectedTags} onChange={changeTags} />
            </FormGrid>
        </FormCol>
    );
};

interface OfferLinkDetailFormProps {
    offerId?: string;
    forceShowOfferSelect?: boolean;
    showUserSelect?: boolean;
    showDomainSelect?: boolean;
    onUrlChange?: (url: string) => void;
}

export const OfferLinkDetailForm: FC<OfferLinkDetailFormProps> = ({
    offerId,
    forceShowOfferSelect,
    showUserSelect = false,
    showDomainSelect = false,
    onUrlChange,
}) => {
    const userUtils = useUser();
    const permissionUtils = usePermission();
    const currentUser = userUtils.getAuthenticatedUser();

    const canViewOwn = permissionUtils.hasPermission(PermissionEnum.OfferLinkViewOwn);
    const canViewOthers = permissionUtils.hasPermission(PermissionEnum.OfferLinkViewOthers);
    const hasOfferViewAccessUnrestrictedPermission = permissionUtils.hasPermission(
        PermissionEnum.OfferViewAccessUnrestricted
    );

    const showOfferSelect = forceShowOfferSelect ?? offerId === undefined;
    const showUserSelectGuarded = showUserSelect && canViewOthers;

    const [selectedOfferId, setSelectedOfferId] = useState<string | undefined>(offerId);

    // Need to fetch offers when showing offer select OR when showing user select (need affiliates)
    const needOffers = showOfferSelect || showUserSelectGuarded;

    const fetchOffers = useCallback((payload: Parameters<typeof offerApi.index>[0]) => {
        return offerApi.index({
            ...payload,
            filter: {
                filter: {},
                include: "userAffiliates,userRequests",
                sort: "title",
            },
        });
    }, []);

    const getAllOffers = useFetchAllPaginatedData(fetchOffers, {}, "offers", { enabled: needOffers });
    const allOffers: OfferData[] = getAllOffers.data ?? [];

    const offers = useMemo(() => {
        return filterOffersByAccess({
            offers: allOffers,
            userId: currentUser?.id,
            hasOfferViewAccessUnrestrictedPermission,
        });
    }, [allOffers, currentUser?.id, hasOfferViewAccessUnrestrictedPermission]);

    // Get the selected offer to access its affiliates
    const selectedOffer = useMemo(() => {
        if (selectedOfferId) {
            return offers.find((o) => o.id === selectedOfferId);
        }
        return offers[0];
    }, [offers, selectedOfferId]);

    const {
        users,
        isLoading: isUsersLoading,
        defaultUserId,
    } = useOfferUserList({
        offer: selectedOffer ?? offers[0],
        showUserSelectGuarded,
        canViewOwn,
        currentUser,
    });

    const isOffersLoading = needOffers && getAllOffers.isLoading;

    // Clear stale URL when no affiliates are available
    useEffect(() => {
        if (!defaultUserId) {
            onUrlChange?.("");
        }
    }, [defaultUserId, onUrlChange]);

    // Still loading - show skeleton
    if (isOffersLoading || isUsersLoading) {
        return <FormSkeleton />;
    }

    // No users available (e.g., private offer with no affiliates) - show message
    if (!defaultUserId) {
        return <NoAffiliatesMessage />;
    }

    const defaultValues: OfferLinkFormData = {
        offer: offerId ?? selectedOffer?.id,
        user: defaultUserId,
        title: undefined,
        domain: DEFAULT_DOMAIN_VALUE,
        url: "",
    };

    return (
        <GeneralFormProvider<OfferLinkFormData>
            schema={formSchema}
            defaultValues={defaultValues}
            id={FormId.OfferLinkDetailForm}
        >
            <OfferLinkDetailFormContent
                offerId={offerId}
                showOfferSelect={showOfferSelect}
                showUserSelect={showUserSelectGuarded ?? false}
                showDomainSelect={showDomainSelect}
                offers={offers}
                users={users}
                defaultUserId={defaultUserId}
                isOffersLoading={isOffersLoading || isUsersLoading}
                onOfferChange={setSelectedOfferId}
                onUrlChange={onUrlChange}
            />
        </GeneralFormProvider>
    );
};

const FormSkeleton = () => (
    <div className="flex flex-col">
        <div className="flex justify-between items-center mb-4">
            <Skeleton.Input className="!w-[110px]" />
            <div className="flex flex-row gap-2">
                <Skeleton.Button className="!w-[90px]" />
                <Skeleton.Button className="!w-[90px]" />
            </div>
        </div>
        <div className="grid grid-cols-2 gap-2 mb-[16px]">
            <Skeleton.Input className="!min-w-[60px] !w-[60px] !h-[20px]" />
            <Skeleton.Input className="!min-w-[60px] !w-[60px] !h-[20px]" />
            <Skeleton.Input className="!w-full !h-[32px] mb-[16px]" />
            <Skeleton.Input className="!w-full !h-[32px] mb-[16px]" />
            <div className="col-span-2">
                <Skeleton.Input className="!min-w-[60px] !w-[60px] !h-[20px]" />
            </div>
            <div className="col-span-2">
                <Skeleton.Input className="!w-full !h-[100px]" />
            </div>
        </div>
    </div>
);

const NoAffiliatesMessage = () => {
    const { t } = usePaldockTranslation("forms.offer-link-detail");
    return <div className="text-gray-500 py-4">{t("messages.no_affiliates")}</div>;
};
